import React, { Component } from 'react';
import { Map } from 'react-leaflet';
import FullscreenControl from 'react-leaflet-fullscreen';
import MapLayersControl from './MapLayerControl';
import PropTypes from 'prop-types';
import sizeMe from 'react-sizeme';
import { createGeoJsonLayer, centerOrFitBounds } from './mapUtils';
import PropertyEditor from './PropertyEditor';

import 'leaflet-draw/dist/leaflet.draw.css';

import L from 'leaflet';
import 'leaflet.polylinemeasure/Leaflet.PolylineMeasure';
import 'leaflet.polylinemeasure/Leaflet.PolylineMeasure.css';
import 'leaflet-easybutton/src/easy-button.css';
import 'leaflet-easybutton/src/easy-button';
import 'leaflet-draw';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const ShowPropsIcon = `
<div style="display: flex; align-items: center; justify-content: center; height: 100%;">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
    <path fill="black" d="M19.14,12.94a7.007,7.007,0,0,0,0-1.88l2.03-1.58a.5.5,0,0,0,.12-.66l-1.92-3.32a.5.5,0,0,0-.61-.22l-2.39.96a7.028,7.028,0,0,0-1.6-.93l-.36-2.54A.5.5,0,0,0,14,2H10a.5.5,0,0,0-.5.42l-.36,2.54a7.028,7.028,0,0,0-1.6.93l-2.39-.96a.5.5,0,0,0-.61.22L2.62,8.82a.5.5,0,0,0,.12.66L4.77,11.06a7.007,7.007,0,0,0,0,1.88L2.74,14.52a.5.5,0,0,0-.12.66l1.92,3.32a.5.5,0,0,0,.61.22l2.39-.96a7.028,7.028,0,0,0,1.6.93l.36,2.54A.5.5,0,0,0,10,22h4a.5.5,0,0,0,.5-.42l.36-2.54a7.028,7.028,0,0,0,1.6-.93l2.39.96a.5.5,0,0,0,.61-.22l1.92-3.32a.5.5,0,0,0-.12-.66ZM12,15.5A3.5,3.5,0,1,1,15.5,12,3.504,3.504,0,0,1,12,15.5Z"/>
  </svg>
</div>`;

const HidePropsIcon = `
<div style="display: flex; align-items: center; justify-content: center; height: 100%;">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
    <path fill="black" d="M19.14,12.94a7.007,7.007,0,0,0,0-1.88l2.03-1.58a.5.5,0,0,0,.12-.66l-1.92-3.32a.5.5,0,0,0-.61-.22l-2.39.96a7.028,7.028,0,0,0-1.6-.93l-.36-2.54A.5.5,0,0,0,14,2H10a.5.5,0,0,0-.5.42l-.36,2.54a7.028,7.028,0,0,0-1.6.93l-2.39-.96a.5.5,0,0,0-.61.22L2.62,8.82a.5.5,0,0,0,.12.66L4.77,11.06a7.007,7.007,0,0,0,0,1.88L2.74,14.52a.5.5,0,0,0-.12.66l1.92,3.32a.5.5,0,0,0,.61.22l2.39-.96a7.028,7.028,0,0,0,1.6.93l.36,2.54A.5.5,0,0,0,10,22h4a.5.5,0,0,0,.5-.42l.36-2.54a7.028,7.028,0,0,0,1.6-.93l2.39.96a.5.5,0,0,0,.61-.22l1.92-3.32a.5.5,0,0,0-.12-.66ZM12,15.5A3.5,3.5,0,1,1,15.5,12,3.504,3.504,0,0,1,12,15.5Z"/>
    <path fill="red" d="M6 6 L18 18 M18 6 L6 18" stroke="red" stroke-width="2"/>
  </svg>
</div>`;

const SyncMapCenterIcon = `
<div style="display: flex; align-items: center; justify-content: center; height: 100%;">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
    <!-- Small circle at center -->
    <circle cx="12" cy="12" r="2" stroke="black" stroke-width="2" fill="none"/>

    <!-- Vertical line (top) -->
    <line x1="12" y1="2"  x2="12" y2="8"  stroke="black" stroke-width="2"/>
    <!-- Vertical line (bottom) -->
    <line x1="12" y1="16" x2="12" y2="22" stroke="black" stroke-width="2"/>

    <!-- Horizontal line (left) -->
    <line x1="2"  y1="12" x2="8"  y2="12" stroke="black" stroke-width="2"/>
    <!-- Horizontal line (right) -->
    <line x1="16" y1="12" x2="22" y2="12" stroke="black" stroke-width="2"/>
  </svg>
</div>`;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const controls = {
    rectangle: true,
    circlemarker: false,
    polygon: { allowIntersection: false, showArea: true },
    polyline: true,
    circle: false,
    marker: true
};

class AnnotationMap extends Component {

    static propTypes = {
        lastMapCenter: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        geoJsonData: PropTypes.object,
        lastMapZoom: PropTypes.number,
        disabled: PropTypes.bool,   // Do not allow editing
        onUpdated: PropTypes.func,
        customMap: PropTypes.object,
        width: PropTypes.string,
        height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        size: PropTypes.shape({
            width: PropTypes.number,
            height: PropTypes.number,
        }),
    }

    constructor(props) {
        super(props);

        const last = JSON.parse(localStorage.getItem("lastAnnotationProperties")) || {};

        const defaultColor = last.stroke || "#0040ff";
        const defaultWeight = last["stroke-width"] ? parseInt(last["stroke-width"], 10) : 2;
        const defaultOpacity = last["stroke-opacity"] ? parseFloat(last["stroke-opacity"]) : 0.8;
        const defaultFillColor = last["fill-color"] || "#0080ff";
        const defaultFillOpacity = last["fill-opacity"] ? parseFloat(last["fill-opacity"]) : 0.2;

        this.state = {
            zoom: props.lastMapZoom || last.zoom || 13,
            mapPosition: props.lastMapCenter || last.mapPosition || [41.35, 2.18],
            selectedLayer: null,
            showPropEditor: false,
            color: defaultColor,
            weight: defaultWeight,
            opacity: defaultOpacity,
            fillColor: defaultFillColor,
            fillOpacity: defaultFillOpacity,
        };


        this.handleShowPropControlClick = this.handleShowPropControlClick.bind(this);
        this.handleHidePropControlClick = this.handleHidePropControlClick.bind(this);
    }

    getCurrentGeoJson = () => ({
        type: 'FeatureCollection',
        features: this.drawnItems
            ? this.drawnItems.getLayers().map(layer => layer.feature).filter(Boolean)
            : [],
    });

    getGeoJsonSignature = (geoJson) => JSON.stringify({
        type: 'FeatureCollection',
        features: Array.isArray(geoJson?.features) ? geoJson.features : [],
    });

    setSelectedLayerState = (selectedLayer) => {
        const featureProps = selectedLayer?.feature?.properties || {};

        this.setState(prevState => ({
            selectedLayer: selectedLayer || null,
            color: featureProps.stroke || prevState.color,
            weight: featureProps['stroke-width'] ?? prevState.weight,
            opacity: featureProps['stroke-opacity'] ?? prevState.opacity,
            fillColor: featureProps['fill-color'] || prevState.fillColor,
            fillOpacity: featureProps['fill-opacity'] ?? prevState.fillOpacity,
        }));
    }

    syncDrawnItemsFromGeoJson = (geoJsonData, shouldRecenter = false) => {
        const map = this.refs.map?.leafletElement;

        if (!map || !this.drawnItems) {
            return;
        }

        this.drawnItems.clearLayers();

        let nextSelectedLayer = null;

        if (geoJsonData && Array.isArray(geoJsonData.features) && geoJsonData.features.length > 0) {
            const geoJsonLayer = createGeoJsonLayer(geoJsonData, this.setState.bind(this), null);
            geoJsonLayer.eachLayer(layer => {
                if (!nextSelectedLayer) {
                    nextSelectedLayer = layer;
                }
                this.drawnItems.addLayer(layer);
            });
        }

        if (shouldRecenter) {
            centerOrFitBounds(map, geoJsonData, this.state.zoom, this.state.mapPosition);
        }

        this.setSelectedLayerState(nextSelectedLayer);
    }

    componentDidMount() {
        this.annotationsMapRef = React.createRef();
        const map = this.refs.map.leafletElement;
        const fc = this.props.geoJsonData;

        // 2) Basic map controls
        map.dragging.enable();
        L.control.polylineMeasure({
            position: 'topleft',
            unit: 'metres',
            showBearings: true,
            clearMeasurementsOnStop: true,
            showClearControl: true,
            showUnitControl: true
        }).addTo(map);
        L.control.scale().addTo(map);

        // 3) Prepare the drawnItems layer group
        this.drawnItems = new L.FeatureGroup();
        map.addLayer(this.drawnItems);

        // 4) Render existing GeoJSON features, if any
        this.syncDrawnItemsFromGeoJson(fc, true);

        // Show Sync Map center button
        L.easyButton({
            position: 'bottomleft',
            states: [
                {
                    stateName: 'sync-map-center',
                    icon: SyncMapCenterIcon,
                    title: 'Sync Map center',
                    onClick: (btn, map) => {
                        this.handleSyncMapCenterClick(map);
                    }
                }
            ]
        }).addTo(map);


        // 5) Set up draw controls

        if (!this.props.disabled) {

            // 6) Show/hide properties button
            L.easyButton({
                position: 'bottomright',
                states: [
                    {
                        stateName: 'show-prop-control-on',
                        icon: ShowPropsIcon,
                        title: 'Show properties',
                        onClick: (btn, map) => {
                            btn.state('show-prop-control-off');
                            this.handleShowPropControlClick(map);
                        }
                    },
                    {
                        stateName: 'show-prop-control-off',
                        icon: HidePropsIcon,
                        title: 'Hide properties',
                        onClick: (btn, map) => {
                            btn.state('show-prop-control-on');
                            this.handleHidePropControlClick(map);
                        }
                    }
                ]
            }).addTo(map);

            map.on(L.Draw.Event.CREATED, this.doDrawActionCreated);
            map.on(L.Draw.Event.EDITED, this.doDrawActionEdited);
            map.on(L.Draw.Event.DELETED, this.doDrawActionDeleted);

            map.on('moveend', () => {
                const center = map?.getCenter();
                const zoom = map?.getZoom();

                if (center && zoom) {
                    const saved = JSON.parse(localStorage.getItem("lastAnnotationProperties")) || {};
                    localStorage.setItem("lastAnnotationProperties", JSON.stringify({ ...saved, mapPosition: [center.lat, center.lng], zoom }));
                    this.setState(prevState => ({ ...prevState, mapPosition: [center.lat, center.lng], zoom }));
                }
            });


            this.drawControl = new L.Control.Draw({
                position: 'bottomright',
                draw: controls,
                edit: { featureGroup: this.drawnItems }
            });
            map.addControl(this.drawControl);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.geoJsonData !== this.props.geoJsonData) {
            const currentSignature = this.getGeoJsonSignature(this.getCurrentGeoJson());
            const nextSignature = this.getGeoJsonSignature(this.props.geoJsonData);

            if (currentSignature !== nextSignature) {
                this.syncDrawnItemsFromGeoJson(this.props.geoJsonData, true);
            }
        }
    }


    doDrawActionCreated = (event) => {

        const { onUpdated } = this.props;
        const layer = event.layer;
        const { layerType } = event;

        // Convert to GeoJSON Feature
        const feature = layer.toGeoJSON();

        // Keep it on the layer so edits can reuse it
        layer.feature = feature;

        layer.on("mouseover", () => {
            layer.openPopup();
        });
        layer.on("mouseout", () => {
            layer.closePopup();
        });

        layer.on("click", () => {
            this.setState({
                selectedLayer: layer,
            });
        });

        // Add to the drawnItems group
        this.drawnItems.addLayer(layer);

        // Apply style visually
        if (typeof layer.setStyle === 'function') {
            layer.setStyle({
                color: this.state.color,
                weight: this.state.weight,
                opacity: this.state.opacity,
                fillColor: this.state.fillColor,
                fillOpacity: this.state.fillOpacity,
            });
        } else if (layerType === 'marker' && layer.setIcon) {
            layer.setIcon(new L.Icon.Default());
        }

        // Mark as selected
        this.setState({ selectedLayer: layer });

        // Notify parent with a single-feature FeatureCollection
        if (onUpdated) {
            const all = this.drawnItems.getLayers().map(l => l.feature);
            onUpdated({
                type: 'edited',
                geoJson: {
                    type: 'FeatureCollection',
                    features: all
                }
            });
        }
    }


    doDrawActionEdited = (event) => {
        const { onUpdated } = this.props;

        const edited = {
            type: 'FeatureCollection',
            features: []
        };

        // Go through all edited layers
        event.layers.eachLayer(layer => {

            const feature = layer.toGeoJSON();

            // Reapply updated style to the layer
            const newProps = {
                'stroke': this.state.color,
                'stroke-width': this.state.weight,
                'stroke-opacity': this.state.opacity,
                'fill-color': this.state.fillColor,
                'fill-opacity': this.state.fillOpacity,
            };

            feature.properties = {
                ...feature.properties,
                ...newProps
            };

            layer.feature = feature; // reattach to layer
            if (typeof layer.setStyle === 'function') {
                layer.setStyle({
                    color: newProps["stroke"],
                    weight: newProps['stroke-width'],
                    opacity: newProps['stroke-opacity'],
                    fillColor: newProps['fill-color'],
                    fillOpacity: newProps['fill-opacity']
                });
            }

            // Replace the corresponding layer in drawnItems
            // Remove old matching layer first (by _leaflet_id or feature._id if needed)
            const layerId = layer._leaflet_id;
            this.drawnItems.eachLayer(existing => {
                if (existing._leaflet_id === layerId) {
                    this.drawnItems.removeLayer(existing);
                }
            });

            this.drawnItems.addLayer(layer); // re-add updated

            edited.features.push(feature); // add to output
        });

        // Update selectedLayer to the last one touched
        const last = edited.features.length ? edited.features[edited.features.length - 1] : null;
        const lastLayer = last
            ? [...this.drawnItems.getLayers()].find(l => l.feature === last)
            : null;

        this.setState({ selectedLayer: lastLayer || null });

        // Notify parent
        if (onUpdated) {
            // send all current features
            const all = this.drawnItems.getLayers().map(l => l.feature);
            onUpdated({
                type: 'edited',
                geoJson: {
                    type: 'FeatureCollection',
                    features: all
                }
            });
        }
    };


    doDrawActionDeleted = (event) => {
        const { onUpdated } = this.props;
        const { selectedLayer } = this.state;

        const deletedLayerIds = new Set();
        event.layers.eachLayer(layer => {
            deletedLayerIds.add(layer._leaflet_id);
        });

        // Build the new array of remaining features
        const remainingLayers = this.drawnItems.getLayers();
        const remainingFeatures = remainingLayers
            .map(layer => layer.feature)
            .filter(Boolean);

        const nextSelectedLayer = selectedLayer && !deletedLayerIds.has(selectedLayer._leaflet_id)
            ? selectedLayer
            : remainingLayers[0] || null;

        // Clear out selectedLayer (pick the first remaining, or null)
        this.setSelectedLayerState(nextSelectedLayer);

        // Notify parent with updated FeatureCollection
        onUpdated?.({
            type: 'edited',
            geoJson: {
                type: 'FeatureCollection',
                features: remainingFeatures
            }
        });
    };


    handleShowPropControlClick = () => {
        this.setState((prevState) => ({ showPropEditor: !prevState.showPropEditor }));
    }

    handleSyncMapCenterClick = (map) => {

      //  const { center, zoom } = this.context;

     //   const saved = JSON.parse(localStorage.getItem("lastMapProps"));
     //   if (saved && saved.center) {
            map.setView(this.props.lastMapCenter, this.state.zoom, { animation: true });
     //   }
    }

    handleHidePropControlClick = (map) => {
        this.setState({ showPropEditor: false });
    }

    updateSelectedLayerStyle = (field, value) => {
        const { selectedLayer } = this.state;
        if (!selectedLayer) return;

        // 1) Prepare style and GeoJSON-compatible properties
        const newStyle = {};
        const newProps = {
            'stroke': this.state.color,
            'stroke-width': this.state.weight,
            'stroke-opacity': this.state.opacity,
            'fill-color': this.state.fillColor,
            'fill-opacity': this.state.fillOpacity,
            'title': selectedLayer.feature?.properties?.featureTitle || ''
        };

        switch (field) {
            case 'color':
                newStyle.color = value;
                newProps.stroke = value;
                break;
            case 'weight':
                newStyle.weight = parseInt(value, 10);
                newProps['stroke-width'] = parseInt(value, 10);
                break;
            case 'opacity':
                newStyle.opacity = parseFloat(value);
                newProps['stroke-opacity'] = parseFloat(value);
                break;
            case 'fillColor':
                newStyle.fillColor = value;
                newProps['fill-color'] = value;
                break;
            case 'fillOpacity':
                newStyle.fillOpacity = parseFloat(value);
                newProps['fill-opacity'] = parseFloat(value);
                break;
            case 'featureTitle':
                newProps.featureTitle = value;
                break;

            default:
                return;
        }

        // 2) Apply style to selected layer (skip if changing title only)
        if (Object.keys(newStyle).length > 0 && typeof selectedLayer.setStyle === 'function') {
            selectedLayer.setStyle(newStyle);
        }

        // 3) Update local component state
        if (field !== 'featureTitle') {
            this.setState({ [field]: value });
        }

        // 4) Save to localStorage (excluding title)
        const saved = JSON.parse(localStorage.getItem("lastAnnotationProperties")) || {};
        const styleKeys = ['stroke', 'stroke-width', 'stroke-opacity', 'fill-color', 'fill-opacity'];
        const styleProps = Object.fromEntries(
            Object.entries(newProps).filter(([k]) => styleKeys.includes(k))
        );
        localStorage.setItem("lastAnnotationProperties", JSON.stringify({ ...saved, ...styleProps }));

        // 5) Update layer’s GeoJSON feature
        const feature = selectedLayer.feature;
        feature.properties = { ...feature.properties, ...newProps };

        // 6) Update popup if title is modified
        if (field === 'featureTitle') {
            if (selectedLayer.getPopup()) {
                selectedLayer.setPopupContent(value);
            } else {
                selectedLayer.bindPopup(value);
            }
        }

        // 7) Notify parent with updated FeatureCollection
        if (this.props.onUpdated) {
            const allFeatures = [];
            this.drawnItems.eachLayer(layer => {
                if (layer.feature) {
                    allFeatures.push(layer.feature);
                }
            });

            this.props.onUpdated({
                type: 'edited',
                geoJson: {
                    type: 'FeatureCollection',
                    features: allFeatures
                }
            });
        }
    };


    render() {
        const { width, height } = this.props.size;

        return (
            <div className="leaflet-container" ref={this.annotationsMapRef} style={{ width: this.props.width, height: this.props.height }}>
                {this.state.showPropEditor && (
                    <PropertyEditor
                        selectedLayer={this.state.selectedLayer}
                        color={this.state.color}
                        weight={this.state.weight}
                        opacity={this.state.opacity}
                        fillColor={this.state.fillColor}
                        fillOpacity={this.state.fillOpacity}
                        updateSelectedLayerStyle={this.updateSelectedLayerStyle}
                    />
                )}
                <Map ref="map" width={width} height={height} onViewportChanged={this.handleViewportChanged}>
                    <MapLayersControl customMap={this.props.customMap} />
                    <FullscreenControl position="topleft" />
                </Map>
            </div>
        );
    }
}

export default sizeMe({ monitorHeight: true, refreshRate: 200 })(AnnotationMap);
